import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

interface Props {
    show: boolean;
    file: Blob;
    onClose: () => void;
    onUploadComplete: (blob: Blob) => void;
}

const ImageCropModal: React.FC<Props> = ({ show, file, onClose, onUploadComplete }) => {
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [uploading, setUploading] = useState(false);

    const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    useEffect(() => {
        if (!file) return;

        const reader = new FileReader();
        const handleLoad = () => setImageSrc(reader.result as string);

        reader.addEventListener('load', handleLoad);
        reader.readAsDataURL(file);

        return () => {
            reader.removeEventListener('load', handleLoad);
        };
    }, [file]);


    const getCroppedImageBlob = async (): Promise<Blob> => {
        const image = new Image();
        image.src = imageSrc!;
        await new Promise(resolve => (image.onload = resolve));

        const canvas = document.createElement('canvas');
        canvas.width = croppedAreaPixels!.width;
        canvas.height = croppedAreaPixels!.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(
            image,
            croppedAreaPixels!.x,
            croppedAreaPixels!.y,
            croppedAreaPixels!.width,
            croppedAreaPixels!.height,
            0,
            0,
            croppedAreaPixels!.width,
            croppedAreaPixels!.height
        );

        return new Promise(resolve => canvas.toBlob(blob => resolve(blob!), 'image/jpeg'));
    };

    const handleUpload = async () => {
        if (!croppedAreaPixels || !imageSrc) return;

        setUploading(true);
        const blob = await getCroppedImageBlob();
        setUploading(false);
        onUploadComplete(blob);
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Crop Image to Square</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {imageSrc && <div style={{ position: 'relative', width: '100%', height: 400 }}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>}
                {!imageSrc && <div>No Image Loaded</div>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleUpload} disabled={!imageSrc || uploading}>
                    {uploading ? <Spinner animation="border" size="sm" /> : 'Upload Cropped Image'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImageCropModal;